import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import moment from 'moment/moment'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useGetReceiverModels } from '../../../../../hooks/useGetReceiverModels'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import { updateInstallationData } from '../../../../../services/ApiService'
import { notifySuccess } from '../../../../../services/ToastifyService/ToastifyService'
import { CheckEquipmentEverywhereRes } from '../../../../../types/equipment.types'
import { RequestType } from '../../../../SingleRequestPage/types/Request.type'
import { REPORT_KEYS } from '../../../../SingleRequestReport/consts/request-report.keys'
import newStore from '../../../components/RequestsTableOptimized/RequestsTableOptimizedStore'
import { newRequestDrawerStore } from '../../../stores/NewRequestDrawerStore'
import {
  changeInstallationSchema,
  getDefaultValues,
  getReportPeriod,
  InstallationDataNewForm,
} from '../helpers/newInstallation.helpers'

export const useInstallationDataNew = (request: RequestType, store?: any) => {
  const [dateError, setDateError] = useState(false)
  const [lastCheckedId, setLastCheckedId] = useState(request.equipmentId)
  const [isEditMode, setIsEditMode] = React.useState(request.status === 5)
  const defaultValues = getDefaultValues(request)
  const invalidate = useInvalidateQueries()
  const { data: receiveresModels } = useGetReceiverModels()
  const {
    control,
    watch,
    reset,
    setError,
    clearErrors,
    handleSubmit,
    trigger,
    formState: { isValid, errors },
    setValue,
  } = useForm<InstallationDataNewForm>({
    defaultValues,
    resolver: zodResolver(changeInstallationSchema),
    mode: 'onChange',
  })

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['update-installation-data', request.id],
    mutationFn: (params: Partial<InstallationDataNewForm>) =>
      updateInstallationData(request.id, params),
    onSuccess: (data) => {
      setIsEditMode(false)
      // для старого дровера
      newStore.updateOrders([data])
      store?.updateDrawerData?.()
      store?.updateOrder?.(data)
      // для нового дровера дилера
      invalidate([
        'requests-table',
        REPORT_KEYS.GET_REPORT_TABLE,
        REPORT_KEYS.ADD,
        REPORT_KEYS.SUITABLE,
        REPORT_KEYS.CREATE_REPORT,
      ])
      newRequestDrawerStore.updateRequest(data)
      notifySuccess('Заявка обновлена')
    },
  })

  const onCheckEquipment = (data?: CheckEquipmentEverywhereRes) => {
    if (data?.specs) {
      setValue('hasCas', !!data.specs.hasCas)
      setValue('receiverSerialNumber', data.specs.sn!)
      setValue('receiverModelId', data.specs.model)
    } else {
      setValue('hasCas', false)
    }

    setValue('smartCardId', null)
    setLastCheckedId(watch('equipmentId'))
  }

  useEffect(() => {
    if (lastCheckedId && lastCheckedId === watch('equipmentId'))
      setLastCheckedId(defaultValues.equipmentId)
  }, [watch('equipmentId')])

  useEffect(() => {
    if (watch('receiverModelId')) trigger()
  }, [watch('receiverModelId')])

  useEffect(() => {
    if (watch('smartCardId') === watch('equipmentId')) {
      setValue('smartCardId', null)
      setValue('hasCas', true)
      trigger()
    }
  }, [watch('smartCardId')])

  useEffect(() => {
    if (!isEditMode) onReset()
  }, [isEditMode])

  const canEditEquipmentId =
    [14].includes(request.status) ||
    (request.status === 6 && [2].includes(request.dataCheckingStatus))
  const canUploadImages = [5].includes(request.status)
  const canEditReceiver = canEditEquipmentId || canUploadImages

  const canEditData =
    (request.status === 6 && [0, 2].includes(request.dataCheckingStatus)) ||
    [14].includes(request.status)
  const showOnlyPhotos = [7, 11].includes(request.status)

  useEffect(() => {
    const reportPeriod = getReportPeriod(request)
    if (reportPeriod.from && moment(watch('installationDate')).isValid()) {
      const isWithinPeriod = moment(watch('installationDate')).isBetween(
        reportPeriod.from,
        reportPeriod.to,
        'day',
        '[]', // включительно с обеих сторон
      )
      if (!isWithinPeriod && request.status === 14) {
        setDateError(true)
        setError('installationDate', { message: 'период отчета не совпадает' })
      } else {
        setDateError(false)
        clearErrors('installationDate')
      }
    }
  }, [watch('installationDate')])

  useEffect(() => {
    if (request.status === 5 && !isEditMode) {
      setIsEditMode(true)
    } else if (request.status !== 5 && !canEditData && isEditMode) {
      setIsEditMode(false)
    }
  }, [request.status, request.dataCheckingStatus])

  useEffect(() => {
    if (
      watch('receiverSerialNumber') === request.receiverSerialNumber &&
      errors.receiverSerialNumber?.message === 'not found receiver'
    ) {
      clearErrors('receiverSerialNumber')
      trigger()
    }
  }, [errors.receiverSerialNumber, isEditMode])

  const onReset = () => {
    reset(defaultValues)
    trigger()
    setIsEditMode(false)
  }

  const isAtLeastOneChanged =
    watch('installationDate') !== defaultValues.installationDate ||
    watch('equipmentId') !== defaultValues.equipmentId ||
    watch('receiverModelId') !== defaultValues.receiverModelId ||
    watch('receiverSerialNumber') !== defaultValues.receiverSerialNumber

  const onSubmit = async (data: InstallationDataNewForm) => {
    const body: Partial<InstallationDataNewForm> = {}

    if (data.installationDate !== defaultValues.installationDate) {
      body.installationDate = data.installationDate
    }
    if (data.equipmentId !== defaultValues.equipmentId) {
      body.equipmentId = data.equipmentId
    }
    const isReceiverChanged =
      data.receiverModelId !== defaultValues.receiverModelId ||
      data.receiverSerialNumber !== defaultValues.receiverSerialNumber
    if (isReceiverChanged) {
      const modelId = getModelId(data.receiverModelId, receiveresModels)
      if (modelId) {
        body.receiverModelId = modelId
        body.receiverSerialNumber = data.receiverSerialNumber
      }
    }

    await mutateAsync(body)
  }

  const modelId = watch('receiverModelId')
  const isEquipmentIdChecked = lastCheckedId === watch('equipmentId')

  return {
    isValid:
      request.status !== 6 && request.dataCheckingStatus !== 0
        ? !errors.installationDate
        : isValid && isEquipmentIdChecked,
    watch,
    modelId,
    isEquipmentIdChecked,
    form: {
      onReset,
      isPending,
      isAtLeastOneChanged,
      isEditMode,
      dateError,
      canUploadImages,
      defaultValues,
      control,
      canEditEquipmentId,
      canEditData,
      handleSubmit,
      setIsEditMode,
      onSubmit,
      showOnlyPhotos,
      receiveresModels,
      canEditReceiver,
      errors,
      setValue,
      equipmentId: watch('equipmentId'),
      onCheckEquipment,
      showCheckedBtn:
        !isEquipmentIdChecked && !errors.equipmentId && !!watch('equipmentId'),
      setError,
      request,
      lastCheckedId,
    },
  }
}

export const getModelId = (value: string, receiveresModels: any[]) =>
  receiveresModels?.find((el: any) => value === el.productName)?.id
